import { useState } from "react";
import { Volume2, RotateCw, ChevronLeft, ChevronRight, X, Check, RotateCcw, PartyPopper } from "lucide-react";
import { api, speak } from "@/lib/api";
import { ThemeIcon } from "@/components/ThemeIcon";
import { WordIcon } from "@/components/WordIcon";
import { useTranslations, Translation } from "@/lib/translations";

export function VocabFlashcards({ words, onClose, onMark, onFinish }) {
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);
  const w = words[i];
  const { map, rtl } = useTranslations(words.slice(0, 30));
  const go = (d) => {
    setFlipped(false);
    setI((i + d + words.length) % words.length);
  };

  const mark = async (known) => {
    api.post("/vocabulary/mark", { word: w.word, known }).catch(() => {});
    onMark(w.word, !known);
    if (i + 1 >= words.length) {
      api.post("/vocabulary/session-complete").catch(() => {});
      onFinish?.();
      return setDone(true);
    }
    go(1);
  };

  if (done)
    return (
      <div className="text-center space-y-5 py-10 rise" data-testid="vocab-flashcards-done">
        <PartyPopper className="w-14 h-14 mx-auto text-orange-500" />
        <p className="font-display text-3xl font-bold uppercase">Série terminée !</p>
        <p className="text-sm text-slate-600" data-testid="vocab-flashcards-goal-note">Objectif fiches du jour validé.</p>
        <button onClick={onClose} data-testid="vocab-flashcards-finish-btn" className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase transition-colors">
          Retour aux mots
        </button>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="vocab-flashcards">
      <div className="flex items-center justify-between">
        <span className="font-bold text-slate-600" data-testid="vocab-flashcard-counter">{i + 1} / {words.length}</span>
        <button onClick={onClose} data-testid="vocab-flashcards-close-btn" className="flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
          <X className="w-5 h-5" /> Fermer
        </button>
      </div>
      <button
        onClick={() => setFlipped(!flipped)}
        data-testid="vocab-flashcard"
        className={`relative w-full min-h-[260px] rounded-3xl p-6 flex flex-col items-center justify-center gap-4 text-center border-4 transition-colors ${
          flipped ? "bg-white border-orange-500" : "bg-slate-900 border-slate-900 text-white"
        }`}
      >
        {w.to_review && (
          <span className="absolute top-3 right-3 text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded-full px-2 py-0.5" data-testid="vocab-flashcard-review-tag">
            À revoir
          </span>
        )}
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase ${flipped ? "text-orange-600" : "text-amber-400"}`}>
          <ThemeIcon theme={w.theme} className="w-4 h-4" /> {w.theme}
        </span>
        {flipped ? (
          <>
            <p className="text-lg font-semibold" data-testid="vocab-flashcard-definition">{w.definition}</p>
            <Translation text={map[w.word]} rtl={rtl} testId="vocab-flashcard-translation" className="text-base" />
            {w.example && <p className="text-sm italic text-slate-600">« {w.example} »</p>}
          </>
        ) : (
          <>
            <WordIcon word={w} size="lg" testId="vocab-flashcard-icon" />
            <p className="font-display text-4xl font-bold" data-testid="vocab-flashcard-word">{w.word}</p>
          </>
        )}
        <span className={`inline-flex items-center gap-1 text-xs ${flipped ? "text-slate-500" : "text-slate-400"}`}>
          <RotateCw className="w-3.5 h-3.5" /> Toucher pour retourner
        </span>
      </button>
      {flipped ? (
        <div className="grid grid-cols-2 gap-2 rise">
          <button onClick={() => mark(false)} data-testid="vocab-flashcard-review-btn" className="h-14 rounded-xl border-2 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 font-bold flex items-center justify-center gap-2 transition-colors">
            <RotateCcw className="w-5 h-5" /> À revoir
          </button>
          <button onClick={() => mark(true)} data-testid="vocab-flashcard-known-btn" className="h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 transition-colors">
            <Check className="w-5 h-5" /> Je sais
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => go(-1)} data-testid="vocab-flashcard-prev-btn" className="h-14 rounded-xl border-2 border-slate-300 hover:border-slate-900 grid place-items-center transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={() => speak(w.word, 0.9)} data-testid="vocab-flashcard-audio-btn" className="h-14 rounded-xl bg-slate-900 text-amber-400 hover:bg-orange-500 hover:text-white grid place-items-center transition-colors">
            <Volume2 className="w-6 h-6" />
          </button>
          <button onClick={() => go(1)} data-testid="vocab-flashcard-next-btn" className="h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white grid place-items-center transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
